/**
 * The Panic Slider: drag from "Mild Coffee Spill" to "Complete Existential
 * Meltdown" and watch the background, copy, and coping mechanism shift live.
 * Also generates a shareable square F!NE mood card via canvas + Web Share API.
 */
class PanicSliderComponent extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[ref="stageData"]');
    if (!dataEl) return;

    /** @type {{level: number, label: string, description: string, coping: string, background: string, text: string, collectionUrl: string|null, collectionTitle: string|null}[]} */
    this.stages = JSON.parse(dataEl.textContent).sort((a, b) => a.level - b.level);
    if (this.stages.length === 0) return;

    this.range = /** @type {HTMLInputElement} */ (this.querySelector('[ref="range"]'));
    this.heading = this.querySelector('[ref="heading"]');
    this.description = this.querySelector('[ref="description"]');
    this.coping = this.querySelector('[ref="coping"]');
    this.level = this.querySelector('[ref="level"]');
    this.shareButton = this.querySelector('[ref="shareButton"]');
    this.shareHint = this.querySelector('[ref="shareHint"]');
    this.networkLinks = {
      x: this.querySelector('[ref="shareX"]'),
      threads: this.querySelector('[ref="shareThreads"]'),
      facebook: this.querySelector('[ref="shareFacebook"]'),
      whatsapp: this.querySelector('[ref="shareWhatsapp"]'),
    };
    this.canvas = /** @type {HTMLCanvasElement} */ (this.querySelector('[ref="canvas"]'));
    this.logoBlack = this.dataset.logoBlack;
    this.logoWhite = this.dataset.logoWhite;

    this.drawer = this.querySelector('[ref="drawer"]');
    this.drawerTitle = this.querySelector('[ref="drawerTitle"]');
    this.drawerBody = this.querySelector('[ref="drawerBody"]');
    this.drawerClose = this.querySelector('[ref="drawerClose"]');
    this.productsShown = Number(this.dataset.productsShown) || 4;
    this.drawerPrefix = this.dataset.drawerPrefix || '';

    /** Rendered markup per level. Cached so revisiting a mood costs nothing. */
    this.markupCache = new Map();
    /** @type {AbortController | null} */
    this.pending = null;
    this.drawerTimer = 0;
    this.drawerDismissed = false;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const shopUrl = (this.dataset.shopUrl || '').replace(/\/$/, '');
    const anchor = this.dataset.panicAnchor;
    this.shareUrl = anchor ? `${shopUrl}/#${anchor}` : shopUrl;
    this.shareDomain = shopUrl.replace(/^https?:\/\//, '');

    if (!this.range) return;

    this.range.min = String(this.stages[0].level);
    this.range.max = String(this.stages[this.stages.length - 1].level);

    this.range.addEventListener('input', () => {
      this.#render();
      this.#scheduleDrawer();
    });
    this.shareButton?.addEventListener('click', () => this.#generateCard());

    this.drawerClose?.addEventListener('click', () => {
      // Closing must not reset the mood — the card and copy stay as they were.
      this.drawerDismissed = true;
      this.#setDrawerOpen(false);
    });

    // `#render` only runs on slider input, so seed the links for stage 1.
    this.#updateNetworkLinks(this.#currentStage());
  }

  disconnectedCallback() {
    clearTimeout(this.drawerTimer);
    this.pending?.abort();
  }

  /**
   * The range fires continuously while dragging. Without this a drag from 1 to
   * 10 would fire ten requests.
   */
  #scheduleDrawer() {
    this.drawerDismissed = false;
    clearTimeout(this.drawerTimer);
    this.drawerTimer = window.setTimeout(() => this.#loadDrawer(), 250);
  }

  /** @param {boolean} open */
  #setDrawerOpen(open) {
    if (this.drawer) this.drawer.dataset.open = String(open);
    this.range?.setAttribute('aria-expanded', String(open));
  }

  async #loadDrawer() {
    if (!this.drawerBody || this.drawerDismissed) return;

    const stage = this.#currentStage();
    const level = stage.level;

    if (this.drawerTitle) {
      this.drawerTitle.textContent = stage.collectionTitle
        ? `${this.drawerPrefix} ${stage.collectionTitle}`.trim()
        : this.drawerPrefix;
    }
    this.#setDrawerOpen(true);

    const cached = this.markupCache.get(level);
    if (cached != null) {
      this.#paint(cached);
      return;
    }

    // A slow response for an earlier mood must never land after a later one.
    this.pending?.abort();
    const controller = new AbortController();
    this.pending = controller;

    const base = stage.collectionUrl || '/collections/all';
    this.drawerBody.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${base}?section_id=mood-drawer-products`, {
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const markup = await response.text();
      this.markupCache.set(level, markup);

      // Guard against a stale win: only paint if this is still the live stage.
      if (this.#currentStage().level === level) this.#paint(markup);
    } catch (error) {
      if (/** @type {Error} */ (error)?.name === 'AbortError') return;
      // A broken drawer must not break the slider. Leave whatever was there.
      if (!this.drawerBody.innerHTML.trim()) this.#setDrawerOpen(false);
    } finally {
      if (this.pending === controller) this.pending = null;
      this.drawerBody.setAttribute('aria-busy', 'false');
    }
  }

  /**
   * Injects the fetched cards, shuffles them, and reveals only the configured
   * number. Reshuffles on every open so repeat visits to a mood stay alive.
   *
   * @param {string} markup
   */
  #paint(markup) {
    if (!this.drawerBody) return;
    this.drawerBody.innerHTML = markup;

    const list = this.drawerBody.querySelector('.mood-drawer-products__list');
    if (!list) return;

    const cards = /** @type {HTMLElement[]} */ ([...list.querySelectorAll('[data-mood-card]')]);
    if (cards.length === 0) return;

    // Reordering content is disorienting for anyone who asked for reduced
    // motion, so they get the collection's own order.
    if (!this.reduceMotion) {
      for (let i = cards.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [cards[i], cards[j]] = [cards[j], cards[i]];
      }
      list.append(...cards);
    }

    cards.forEach((card, index) => {
      if (index < this.productsShown) card.removeAttribute('data-hidden');
      else card.setAttribute('data-hidden', '');
    });
  }

  /**
   * @returns {{level: number, label: string, description: string, coping: string, background: string, text: string, collectionUrl: string|null, collectionTitle: string|null}}
   */
  #currentStage() {
    const level = Number(this.range.value);
    return this.stages.find((stage) => stage.level === level) ?? this.stages[0];
  }

  #render() {
    const stage = this.#currentStage();

    if (this.heading) this.heading.textContent = stage.label;
    if (this.description) this.description.textContent = stage.description;
    if (this.coping) this.coping.textContent = stage.coping;
    const max = this.stages[this.stages.length - 1].level;
    if (this.level) this.level.textContent = `Panic level: ${stage.level} / ${max}`;

    this.style.setProperty('--panic-bg', stage.background);
    this.style.setProperty('--panic-text', stage.text);

    if (this.shareHint) this.shareHint.hidden = true;

    this.#updateNetworkLinks(stage);
  }

  /**
   * Rewrites the per-network share links for the current mood.
   *
   * These carry a LINK, not the image — no web share intent accepts a file.
   * Instagram has no intent URL at all, which is why it isn't in the row: the
   * OS share sheet is the only route to it.
   *
   * @param {{label: string}} stage
   */
  #updateNetworkLinks(stage) {
    if (!this.networkLinks) return;

    const url = this.shareUrl || '';
    const text = `Current mood: ${stage.label}. Diagnosed by F!NE.`;
    const encodedText = encodeURIComponent(text);
    const encodedUrl = encodeURIComponent(url);

    const hrefs = {
      x: `https://x.com/intent/post?text=${encodedText}&url=${encodedUrl}`,
      threads: `https://www.threads.net/intent/post?text=${encodeURIComponent(`${text} ${url}`)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    };

    for (const [network, link] of Object.entries(this.networkLinks)) {
      if (link && hrefs[network]) link.href = hrefs[network];
    }
  }

  async #generateCard() {
    if (!this.canvas) return;

    const stage = this.#currentStage();
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    const size = this.canvas.width;
    const headingFont =
      getComputedStyle(document.documentElement).getPropertyValue('--font-heading--family').trim() ||
      'sans-serif';
    const bodyFont =
      getComputedStyle(document.documentElement).getPropertyValue('--font-body--family').trim() || 'sans-serif';

    if (document.fonts?.ready) await document.fonts.ready;

    ctx.fillStyle = stage.background;
    ctx.fillRect(0, 0, size, size);
    ctx.textAlign = 'center';
    ctx.fillStyle = stage.text;

    const logoSrc = this.#isLightColor(stage.text) ? this.logoWhite : this.logoBlack;
    const logo = logoSrc ? await this.#loadImage(logoSrc).catch(() => null) : null;

    if (logo) {
      const logoWidth = 260;
      const logoHeight = logoWidth * (logo.naturalHeight / logo.naturalWidth);
      ctx.drawImage(logo, (size - logoWidth) / 2, 55, logoWidth, logoHeight);
    } else {
      ctx.font = `900 44px ${headingFont}`;
      ctx.fillText('F!NE', size / 2, 130);
    }

    ctx.font = `700 28px ${bodyFont}`;
    ctx.fillText(`MOOD LEVEL ${stage.level} / 10`, size / 2, 200);

    this.#wrapText(ctx, stage.label, size / 2, 420, size - 140, 68, `900 60px ${headingFont}`);
    this.#wrapText(ctx, stage.description, size / 2, 600, size - 200, 38, `400 26px ${bodyFont}`);
    this.#wrapText(ctx, `Coping mechanism: ${stage.coping}`, size / 2, 760, size - 200, 32, `700 22px ${bodyFont}`);

    this.#drawCtaButton(ctx, stage, size, bodyFont);

    if (this.shareDomain) {
      ctx.fillStyle = stage.text;
      ctx.font = `700 22px ${bodyFont}`;
      ctx.fillText(this.shareDomain, size / 2, size - 60);
    }

    const blob = await new Promise((resolve) => this.canvas.toBlob(resolve, 'image/png'));
    if (!blob) return;

    const file = new File([blob], `fine-mood-${stage.level}.png`, { type: 'image/png' });

    // Three tiers, best first. The image never touches disk unless it has to.
    //
    // 1. OS share sheet with the file  — mobile and modern desktop. Instagram,
    //    X, WhatsApp etc. appear as targets with the image already attached.
    // 2. Clipboard as a PNG            — desktop Chrome/Edge. The user pastes
    //    straight into a composer, which beats hunting in Downloads.
    // 3. Download                      — Firefox, older Safari, last resort.
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'My F!NE Mood',
          text: `Current mood: ${stage.label}`,
          ...(this.shareUrl ? { url: this.shareUrl } : {}),
        });
        return;
      } catch (error) {
        // A cancelled share sheet is a decision, not a failure — don't then
        // dump a file in their Downloads folder.
        if (/** @type {DOMException} */ (error)?.name === 'AbortError') return;
      }
    }

    if (await this.#copyToClipboard(blob)) {
      this.#setHint('Copied. Paste it straight into your post.');
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fine-mood-${stage.level}.png`;
    link.click();
    URL.revokeObjectURL(url);

    this.#setHint('Card saved — share it wherever you like.');
  }

  /**
   * @param {Blob} blob
   * @returns {Promise<boolean>} whether the image made it to the clipboard
   */
  async #copyToClipboard(blob) {
    // Firefox has no ClipboardItem for images; Safari's support is conditional.
    // Feature-detect rather than sniff, and treat any throw as "not supported".
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') return false;
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      return true;
    } catch {
      return false;
    }
  }

  /** @param {string} message */
  #setHint(message) {
    if (!this.shareHint) return;
    this.shareHint.hidden = false;
    this.shareHint.textContent = message;
  }

  /**
   * Draws a pill-shaped CTA button onto the card, inviting whoever sees the
   * shared image to generate their own. Filled with the stage's text color
   * so it always reads clearly against that stage's background.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {{background: string, text: string}} stage
   * @param {number} size
   * @param {string} bodyFont
   */
  #drawCtaButton(ctx, stage, size, bodyFont) {
    const label = 'Make Your Own Mood Card →';
    const buttonWidth = 640;
    const buttonHeight = 76;
    const buttonX = (size - buttonWidth) / 2;
    const buttonY = 860;
    const radius = buttonHeight / 2;

    ctx.fillStyle = stage.text;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(buttonX, buttonY, buttonWidth, buttonHeight, radius);
    } else {
      ctx.rect(buttonX, buttonY, buttonWidth, buttonHeight);
    }
    ctx.fill();

    ctx.fillStyle = stage.background;
    ctx.font = `700 30px ${bodyFont}`;
    ctx.textBaseline = 'middle';
    ctx.fillText(label, size / 2, buttonY + buttonHeight / 2 + 2);
    ctx.textBaseline = 'alphabetic';
  }

  /**
   * @param {string} hex
   * @returns {boolean}
   */
  #isLightColor(hex) {
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128;
  }

  /**
   * @param {string} src
   * @returns {Promise<HTMLImageElement>}
   */
  #loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {string} text
   * @param {number} x
   * @param {number} y
   * @param {number} maxWidth
   * @param {number} lineHeight
   * @param {string} font
   */
  #wrapText(ctx, text, x, y, maxWidth, lineHeight, font) {
    ctx.font = font;
    const words = text.split(' ');
    let line = '';
    let cursorY = y;

    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(testLine).width > maxWidth) {
        ctx.fillText(line, x, cursorY);
        line = word;
        cursorY += lineHeight;
      } else {
        line = testLine;
      }
    }

    if (line) ctx.fillText(line, x, cursorY);
  }
}

if (!customElements.get('panic-slider-component')) {
  customElements.define('panic-slider-component', PanicSliderComponent);
}
